import {
  EMPTY_SELECTION,
  EMPTY_SUMMARY,
  type DrilledEdge,
  type Point2,
  type Selection,
  type SelectionMergeMode,
  type SelectionRef,
  type Store,
  type ViewportState,
  createLens,
  drillSelection,
  mergeSelection,
  walkChain,
  selectionKey,
  selectionOf,
  summarizeSelection,
} from '@frontend/viewer-core';

import { PICK_TOLERANCE_PX } from '../constants';
import { coversWholeLoop } from './coversWholeLoop';
import { createMemoizedRolePolicy } from './rolePolicy';
import {
  NO_PICK,
  type DrawingModel,
  type DxfSessionState,
  type MappingAssignments,
  type SelectionGranularity,
  type SelectionManager,
} from './types';

/** True when two picks would highlight and select exactly the same things. */
const isSamePick = (
  a: readonly SelectionRef[],
  b: readonly SelectionRef[],
): boolean =>
  a === b ||
  (a.length === b.length &&
    a.every((ref, index) => selectionKey(ref) === selectionKey(b[index])));

export const createSelectionSlice = (
  state: Store<DxfSessionState>,
): Store<Selection> =>
  createLens(
    state,
    (current) => current.selection,
    (current, selection) => ({ ...current, selection }),
  );

export const createHoveredSlice = (
  state: Store<DxfSessionState>,
): Store<readonly SelectionRef[]> =>
  createLens(
    state,
    (current) => current.hovered,
    (current, hovered) => ({ ...current, hovered }),
  );

type Dependencies = {
  /** Shared with `layerController` — hiding a layer prunes through here too. */
  selection: Store<Selection>;
  hovered: Store<readonly SelectionRef[]>;
  getHiddenLayers: () => ReadonlySet<number>;
  /**
   * The mapping in force, or `null` on a file with none.
   *
   * A getter rather than a snapshot: loop chaining is recomputed against it on
   * every click, which is the whole reason the model is not re-derived when a
   * role changes.
   */
  getAssignments: () => MappingAssignments | null;
  /**
   * What a click picks right now.
   *
   * A getter rather than the store, deliberately: this is where the Alt
   * modifier's temporary inversion will be layered in, and reading through a
   * function means that arrives without this controller — or anything that calls
   * `pickAt` — changing at all.
   */
  getGranularity: () => SelectionGranularity;
  getModel: () => DrawingModel | null;
  viewport: {
    getState(): ViewportState;
    screenToWorld(screen: Point2): Point2;
  };
};

/**
 * What is picked, hovered, and drilled into.
 *
 * Owns `drilledEdge` itself rather than exposing it: it is not one of the
 * session's stores because nothing outside this file needs to react to it,
 * only to the rule it enforces — every entry point that can leave it stale
 * (switching selection, clearing, loading a new drawing) goes through this
 * controller's own methods instead of a shared mutable cell.
 */
export const createSelectionController = ({
  selection,
  hovered,
  getHiddenLayers,
  getAssignments,
  getGranularity,
  getModel,
  viewport,
}: Dependencies): SelectionManager => {
  let drilledEdge: DrilledEdge = null;
  const getRolePolicy = createMemoizedRolePolicy();

  /** The nearest geometry to a screen point, with the chunk it belongs to. */
  const hitAt = (screenPoint: Point2) => {
    const model = getModel();
    if (!model) {
      return null;
    }
    const { scale } = viewport.getState();
    return model.index.pickAt(viewport.screenToWorld(screenPoint), {
      tolerance: PICK_TOLERANCE_PX / (scale || 1),
      hiddenLayers: getHiddenLayers(),
    });
  };

  const endDrillIfUncovered = (refs: Iterable<SelectionRef>) => {
    const drilled = drilledEdge;
    if (!drilled) {
      return;
    }
    for (const ref of refs) {
      if (
        ref.kind === 'edge' &&
        ref.loopId === drilled.loopId &&
        ref.chunkIndex === drilled.chunkIndex
      ) {
        return;
      }
    }
    // Selecting elsewhere ends the drill: a later double-click there should
    // open that edge, not jump back out to a loop the user has left behind.
    drilledEdge = null;
  };

  const set = (refs: readonly SelectionRef[]) => {
    endDrillIfUncovered(refs);
    selection.setState(() => selectionOf(refs));
  };

  const merge = (refs: readonly SelectionRef[], mode: SelectionMergeMode) => {
    if (mode === 'replace') {
      set(refs);
      return;
    }
    const loops = getModel()?.loops;
    selection.setState((previous) =>
      mergeSelection(previous, refs, mode, loops),
    );
    endDrillIfUncovered(selection.getState().values());
  };

  return {
    getState: selection.getState,
    subscribe: selection.subscribe,
    set,
    merge,
    clear() {
      drilledEdge = null;
      selection.setState(() => EMPTY_SELECTION);
      hovered.setState((previous) =>
        previous.length === 0 ? previous : NO_PICK,
      );
    },

    setHovered(refs) {
      // Compared by key rather than by reference: `pickAt` builds a fresh array
      // on every pointer move, so without this the accent overlay would rebuild
      // its buffers for every mouse event over unchanged geometry.
      hovered.setState((previous) =>
        isSamePick(previous, refs) ? previous : refs,
      );
    },

    pickAt(screenPoint) {
      const hit = hitAt(screenPoint);
      if (!hit) {
        return NO_PICK;
      }
      // Under edge granularity a click takes exactly the run the pointer is
      // over, and nothing else has to be worked out.
      if (getGranularity() === 'edge') {
        return [
          { kind: 'edge', loopId: hit.loopId, chunkIndex: hit.chunkIndex },
        ];
      }

      // Under loop granularity it takes the role-aware chain through that run:
      // as far as the geometry goes without changing role and without an
      // ambiguous junction. That is *not* the same as the stitched loop the hit
      // landed in — a role boundary splits one, and a junction the stitcher had
      // to stop at can join two — so the answer is spelled out as the chunks it
      // actually covers rather than named by a single `loopId`.
      const model = getModel();
      if (!model) {
        return NO_PICK;
      }
      const chain = walkChain(
        model.chunkGraph,
        { loopId: hit.loopId, chunkIndex: hit.chunkIndex },
        {
          keyOf: getRolePolicy({
            model,
            assignments: getAssignments(),
            hiddenLayers: getHiddenLayers(),
          }).chainKeyOf,
        },
      );

      // The common case by far: the chain is the whole of one stitched loop, so
      // it collapses back to a single loop ref. Worth detecting rather than
      // always emitting chunks, because a loop ref is what carries area and a
      // count of one into the details overlay — and on an unmapped drawing every
      // chain is this case.
      return coversWholeLoop(model, chain)
        ? [{ kind: 'loop', loopId: hit.loopId }]
        : chain.map(({ loopId, chunkIndex }) => ({
            kind: 'edge' as const,
            loopId,
            chunkIndex,
          }));
    },

    drillAt(screenPoint, mode = 'replace') {
      const hit = hitAt(screenPoint);
      if (!hit) {
        return;
      }
      if (mode !== 'replace') {
        const edgeRef = {
          kind: 'edge' as const,
          loopId: hit.loopId,
          chunkIndex: hit.chunkIndex,
        };
        drilledEdge = {
          loopId: hit.loopId,
          chunkIndex: hit.chunkIndex,
        };
        merge([edgeRef], mode);
        return;
      }
      const { ref, drilled } = drillSelection(hit, drilledEdge);
      drilledEdge = drilled;
      set([ref]);
    },

    summarize() {
      const model = getModel();
      return model
        ? summarizeSelection(
            selection.getState(),
            model.loops,
            model.chunkGraph,
            getRolePolicy({
              model,
              assignments: getAssignments(),
              hiddenLayers: getHiddenLayers(),
            }).chainKeyOf,
          )
        : EMPTY_SUMMARY;
    },
  };
};
